import * as THREE from 'three';
import { mat, spriteLayer, groundUnder, inFrontOfPlayer } from './common.js';

/**
 * Care Package — a physical crate you have to go and stand on.
 *
 * This is the one streak whose value is not what it does but what it costs: it
 * puts a fixed, smoking, highly visible object on the map and asks the player to
 * be in that exact spot for a second. What comes out is drawn from a weighted
 * table that skews above its own price, so the gamble is worth taking.
 *
 * The crate is claimed with the existing `use` binding (F). Nothing else in the
 * game uses it yet, so there is no ambiguity.
 */

export const TUNING = {
  dropDistance: 9,
  dropHeight: 62,
  fallSpeed: 17, // m/s under the chute
  claimRadius: 2.4,
  claimTime: 0.9, // s of holding F
  lifetime: 75, // s before it is written off
  crateSize: 0.9,

  /** What can come out. Weighted; deliberately skewed above the crate's own cost. */
  table: [
    ['uav', 3],
    ['sentry', 3],
    ['napalm', 3],
    ['helo', 2],
    ['gunship', 1],
  ],

  smokePuffs: 22,
  smokeLife: 2.6,
  smokeRise: 3.4,
};

const _p = new THREE.Vector3();

export class CarePackage {
  static id = 'package';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.root = null;
    this.state = 'idle';
  }

  _build() {
    if (this.root) return;
    const g = new THREE.Group();

    const box = new THREE.Mesh(
      new THREE.BoxGeometry(TUNING.crateSize, TUNING.crateSize * 0.78, TUNING.crateSize),
      mat('crate')
    );
    box.position.y = TUNING.crateSize * 0.39;
    box.castShadow = false;
    g.add(box);

    // Banding, so it reads as a crate and not as a cardboard cube.
    for (const axis of ['x', 'z']) {
      const band = new THREE.Mesh(
        new THREE.BoxGeometry(
          axis === 'x' ? TUNING.crateSize * 1.02 : 0.09,
          TUNING.crateSize * 0.8,
          axis === 'x' ? 0.09 : TUNING.crateSize * 1.02
        ),
        mat('hullDark')
      );
      band.position.y = TUNING.crateSize * 0.39;
      g.add(band);
    }

    const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), mat('accent'));
    beacon.position.y = TUNING.crateSize * 0.82;
    g.add(beacon);
    this.beacon = beacon;

    // A stubby chute that only exists during the fall.
    const chute = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({ color: 0xd8d2c0, roughness: 1, side: THREE.DoubleSide })
    );
    chute.position.y = 2.6;
    g.add(chute);
    this.chute = chute;

    for (const s of [-1, 1]) {
      const line = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 2.2, 4), mat('hullDark'));
      line.position.set(s * 0.42, 1.55, 0);
      line.rotation.z = -s * 0.32;
      g.add(line);
      (this.lines ??= []).push(line);
    }

    this.light = new THREE.PointLight(0xff8a3d, 0, 8, 2);
    this.light.position.y = 1.0;
    g.add(this.light);

    g.name = 'streak-package';
    g.visible = false;
    this.ctx.scene.add(g);
    this.root = g;
  }

  deploy() {
    this._build();
    const target = inFrontOfPlayer(this.ctx, TUNING.dropDistance, _p);
    // If the spot in front is a wall or a rooftop edge, fall back to the player's
    // own feet — a crate that lands inside geometry is unclaimable.
    const gy = groundUnder(this.ctx, target.x, target.z, target.y + 40, 80);
    this.landing = new THREE.Vector3(target.x, gy, target.z);
    this.root.position.set(this.landing.x, this.landing.y + TUNING.dropHeight, this.landing.z);
    this.root.visible = true;
    this.state = 'falling';
    this.t = 0;
    this.claim = 0;
    this._chuteOn(true);
    this._promptShown = false;

    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.7, pitch: 0.85 });
    return true;
  }

  _chuteOn(on) {
    this.chute.visible = on;
    for (const l of this.lines) l.visible = on;
  }

  update(dt, elapsed) {
    this.t += dt;
    const root = this.root;

    if (this.state === 'falling') {
      root.position.y -= TUNING.fallSpeed * dt;
      root.rotation.y += dt * 0.5;
      this.chute.scale.setScalar(1 + Math.sin(elapsed * 3) * 0.05);
      if (root.position.y <= this.landing.y) {
        root.position.y = this.landing.y;
        root.rotation.y = Math.round(root.rotation.y / (Math.PI / 2)) * (Math.PI / 2);
        this.state = 'landed';
        this.t = 0;
        this._chuteOn(false);
        this.ctx.events.emit('audio:play', {
          id: 'impact_metal',
          position: this.landing.clone(),
          volume: 1,
          pitch: 0.6,
        });
        this.ctx.events.emit('hit:surface', {
          point: this.landing.clone(),
          normal: new THREE.Vector3(0, 1, 0),
          material: 'dirt',
          entity: null,
        });
      }
      return true;
    }

    /* ---- landed: smoke, beacon, claim ---- */
    const p = this.ctx.player;
    const near = p && !p.dead && p.position.distanceTo(root.position) < TUNING.claimRadius;

    this.light.intensity = 2.4 + Math.sin(elapsed * 6) * 1.6;
    this.beacon.material = (elapsed * 3) % 1 > 0.5 ? mat('accent') : mat('accentDim');
    this._smoke(elapsed);

    if (near) {
      const holding = this.ctx.input?.action?.('use');
      if (holding) this.claim += dt;
      else this.claim = Math.max(0, this.claim - dt * 2);
      this.sys.setPrompt(
        this.claim > 0
          ? `CLAIMING  ${Math.round((this.claim / TUNING.claimTime) * 100)}%`
          : '[F]  CLAIM CARE PACKAGE',
        this.claim / TUNING.claimTime
      );
      this._promptShown = true;
      if (this.claim >= TUNING.claimTime) {
        this._open();
        return false;
      }
    } else if (this._promptShown) {
      this.sys.setPrompt(null);
      this._promptShown = false;
      this.claim = 0;
    }

    if (this.t >= TUNING.lifetime) {
      this.end();
      return false;
    }
    return true;
  }

  _smoke(elapsed) {
    const layer = spriteLayer(this.ctx, 'pkgsmoke', {
      texture: 'smoke', count: 40, size: 2.2, color: 0xffb27a, additive: false,
    });
    layer.begin();
    const b = this.root.position;
    for (let i = 0; i < TUNING.smokePuffs; i++) {
      const ph = (elapsed * 0.5 + i * 0.41) % TUNING.smokeLife;
      const k = ph / TUNING.smokeLife;
      const ang = i * 2.399;
      layer.add(
        b.x + Math.cos(ang) * (0.25 + k * 1.5),
        b.y + 0.6 + k * TUNING.smokeRise,
        b.z + Math.sin(ang) * (0.25 + k * 1.5),
        (1 - k) * 0.6,
        0.5 + k * 1.9
      );
    }
    layer.end();
  }

  _open() {
    const roll = this.ctx.rng ? this.ctx.rng() : Math.random();
    let total = 0;
    for (const [, w] of TUNING.table) total += w;
    let acc = 0;
    let picked = TUNING.table[0][0];
    for (const [id, w] of TUNING.table) {
      acc += w / total;
      if (roll <= acc) {
        picked = id;
        break;
      }
    }

    this.sys.setPrompt(null);
    this.ctx.events.emit('audio:play', { id: 'ui_kill', volume: 0.9, pitch: 0.8 });
    this.end();
    // Straight into the tray, as a normal earned streak — including the callout.
    this.sys.grant(picked, { source: 'package' });
  }

  end() {
    if (!this.root) return;
    this.root.visible = false;
    this.light.intensity = 0;
    this.state = 'idle';
    if (this._promptShown) {
      this.sys.setPrompt(null);
      this._promptShown = false;
    }
    const layer = spriteLayer(this.ctx, 'pkgsmoke');
    layer.begin();
    layer.end();
  }

  status() {
    return this.state === 'falling' ? 'INBOUND' : 'ON DECK';
  }

  dispose() {
    if (!this.root) return;
    this.root.parent?.remove(this.root);
    this.root.traverse((o) => o.geometry?.dispose?.());
    this.root = null;
  }
}
